import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import UploadMethodSelector from './components/UploadMethodSelector';
import CSVUploadZone from './components/CSVUploadZone';
import BatchPropertyCard from './components/BatchPropertyCard';
import ProgressTracker from './components/ProgressTracker';
import PreviewModal from './components/PreviewModal';

const DRAFT_KEY = 'bulk_upload_draft';

const emptyProperty = () => ({
  address: '', price: '', bedrooms: '', bathrooms: '',
  type: '', description: '', photos: []
});

const BulkUploadProperties = () => {
  const navigate = useNavigate();
  const [activeMethod, setActiveMethod] = useState('csv');
  const [batchProperties, setBatchProperties] = useState([emptyProperty()]);
  const [csvProperties, setCsvProperties] = useState([]);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [draftSaved, setDraftSaved] = useState(false);
  const [draftRecovered, setDraftRecovered] = useState(false);

  // Recover draft on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(DRAFT_KEY);
      if (saved) {
        const draft = JSON.parse(saved);
        if (draft?.method) setActiveMethod(draft?.method);
        if (draft?.batchProperties?.length > 0) {
          setBatchProperties(draft?.batchProperties);
          setDraftRecovered(true);
        }
      }
    } catch (e) { /* ignore */ }
  }, []);

  // Auto-save draft
  useEffect(() => {
    const timer = setTimeout(() => {
      try {
        localStorage.setItem(DRAFT_KEY, JSON.stringify({ method: activeMethod, batchProperties }));
        setDraftSaved(true);
        setTimeout(() => setDraftSaved(false), 2000);
      } catch (e) { /* ignore */ }
    }, 1500);
    return () => clearTimeout(timer);
  }, [activeMethod, batchProperties]);

  const addProperty = () => setBatchProperties(prev => [...prev, emptyProperty()]);

  const updateProperty = (index, updated) => {
    setBatchProperties(prev => prev?.map((p, i) => i === index ? updated : p));
  };

  const removeProperty = (index) => {
    setBatchProperties(prev => prev?.filter((_, i) => i !== index));
  };

  const handleGenerateAI = async (index) => {
    // AI generation logic placeholder
    const property = batchProperties?.[index];
    if (property?.address && property?.type) {
      // Simulate AI description generation
      const aiDescription = `Beautiful ${property?.type} located at ${property?.address}. This property features ${property?.bedrooms} bedrooms and ${property?.bathrooms} bathrooms.`;
      updateProperty(index, { ...property, description: aiDescription });
    }
  };

  const handleCSVUpload = (properties) => {
    setCsvProperties(properties);
  };

  const currentProperties = activeMethod === 'csv' ? csvProperties : batchProperties;
  const validCount = currentProperties?.filter(p => p?.address && p?.price && p?.bedrooms && p?.bathrooms && p?.type)?.length;

  const handlePublish = async (properties) => {
    setShowPreview(false);
    setUploadStatus('uploading');
    await new Promise(r => setTimeout(r, 2000));
    setUploadStatus('success');
    localStorage.removeItem(DRAFT_KEY);
    setTimeout(() => navigate('/agent-dashboard'), 2500);
  };

  const clearDraft = () => {
    localStorage.removeItem(DRAFT_KEY);
    setBatchProperties([emptyProperty()]);
    setCsvProperties([]);
    setDraftRecovered(false);
  };

  return (
    <div className="min-h-screen" style={{ background: '#050A14' }}>
      <Helmet>
        <title>Bulk Upload Properties | ListingLens</title>
      </Helmet>
      {/* Background */}
      <div className="fixed inset-0 pointer-events-none" style={{ zIndex: 0 }}>
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 20% 20%, rgba(0,212,255,0.04) 0%, transparent 50%),
                           radial-gradient(circle at 80% 80%, rgba(123,47,255,0.04) 0%, transparent 50%)`,
        }} />
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(rgba(0,212,255,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(0,212,255,0.02) 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }} />
      </div>
      <div className="relative z-10">
        {/* Header */}
        <div className="sticky top-0 z-40" style={{ background: 'rgba(5,10,20,0.95)', backdropFilter: 'blur(20px)', borderBottom: '1px solid rgba(0,212,255,0.15)' }}>
          <div className="max-w-5xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate('/agent-dashboard')} className="p-2 rounded-lg hover:bg-white/10 transition-colors">
                <Icon name="ArrowLeft" size={18} color="#94A3B8" />
              </button>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(0,212,255,0.2), rgba(123,47,255,0.2))' }}>
                  <Icon name="Upload" size={16} color="#00D4FF" />
                </div>
                <span className="text-white font-bold text-lg">Bulk Upload</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {draftSaved && (
                <div className="flex items-center gap-1.5 text-xs text-green-400">
                  <Icon name="Save" size={12} color="#22C55E" />
                  Draft saved
                </div>
              )}
              <button
                onClick={() => navigate('/create-listing')}
                className="hidden md:flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-300 border border-white/10 hover:bg-white/5 transition-colors"
              >
                <Icon name="Plus" size={14} color="#94A3B8" />
                Single Listing
              </button>
            </div>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-4 md:px-6 py-8">
          {/* Draft Recovery Banner */}
          {draftRecovered && (
            <div className="mb-6 flex items-center justify-between p-4 rounded-xl" style={{ background: 'rgba(0,212,255,0.05)', border: '1px solid rgba(0,212,255,0.2)' }}>
              <div className="flex items-center gap-3">
                <Icon name="RotateCcw" size={18} color="#00D4FF" />
                <div>
                  <p className="text-white text-sm font-medium">Draft recovered</p>
                  <p className="text-slate-400 text-xs">Your previous session has been restored</p>
                </div>
              </div>
              <button onClick={clearDraft} className="text-xs text-slate-400 hover:text-white transition-colors px-3 py-1.5 rounded-lg hover:bg-white/10">
                Clear Draft
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Page Title */}
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">Bulk Upload Properties</h1>
                <p className="text-slate-400">Upload multiple property listings at once via CSV import or batch form entry.</p>
              </div>

              {/* Method Selector */}
              <UploadMethodSelector activeMethod={activeMethod} onMethodChange={setActiveMethod} />

              {/* CSV Upload */}
              {activeMethod === 'csv' && (
                <div className="p-5 rounded-2xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <h2 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <Icon name="FileSpreadsheet" size={18} color="#00D4FF" />
                    CSV Import
                  </h2>
                  <CSVUploadZone onUploadComplete={handleCSVUpload} />
                </div>
              )}

              {/* Batch Form */}
              {activeMethod === 'batch' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h2 className="text-white font-semibold flex items-center gap-2">
                      <Icon name="LayoutList" size={18} color="#A78BFA" />
                      Batch Form Entry
                      <span className="text-xs text-slate-400 font-normal">({batchProperties?.length} {batchProperties?.length === 1 ? 'property' : 'properties'})</span>
                    </h2>
                    <button
                      onClick={addProperty}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all"
                      style={{ background: 'rgba(167,139,250,0.1)', border: '1px solid rgba(167,139,250,0.3)', color: '#A78BFA' }}
                    >
                      <Icon name="Plus" size={14} color="#A78BFA" />
                      Add Property
                    </button>
                  </div>

                  {batchProperties?.map((property, index) => (
                    <BatchPropertyCard
                      key={index}
                      index={index}
                      property={property}
                      onUpdate={updateProperty}
                      onRemove={removeProperty}
                      onGenerateAI={handleGenerateAI}
                    />
                  ))}

                  <button
                    onClick={addProperty}
                    className="w-full flex items-center justify-center gap-2 p-4 rounded-xl border-2 border-dashed border-white/15 hover:border-purple-400/40 hover:bg-purple-400/5 transition-all text-slate-400 hover:text-purple-400"
                  >
                    <Icon name="Plus" size={16} />
                    <span className="text-sm font-medium">Add Another Property</span>
                  </button>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-4">
              {/* Progress Tracker */}
              <ProgressTracker properties={currentProperties} uploadStatus={uploadStatus} />

              {/* Action Buttons */}
              <div className="space-y-3">
                <button
                  onClick={() => setShowPreview(true)}
                  disabled={validCount === 0}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{ background: validCount > 0 ? 'linear-gradient(135deg, #00D4FF, #7B2FFF)' : 'rgba(255,255,255,0.1)', color: validCount > 0 ? '#050A14' : '#94A3B8' }}
                >
                  <Icon name="Eye" size={16} color={validCount > 0 ? '#050A14' : '#94A3B8'} />
                  Preview & Publish ({validCount})
                </button>

                <button
                  onClick={() => {
                    localStorage.setItem(DRAFT_KEY, JSON.stringify({ method: activeMethod, batchProperties }));
                    setDraftSaved(true);
                    setTimeout(() => setDraftSaved(false), 2000);
                  }}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-medium text-sm border transition-all hover:bg-white/5"
                  style={{ border: '1px solid rgba(255,255,255,0.1)', color: '#94A3B8' }}
                >
                  <Icon name="Save" size={16} color="#94A3B8" />
                  Save as Draft
                </button>
              </div>

              {/* Tips */}
              <div className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <p className="text-white text-sm font-semibold mb-3 flex items-center gap-2">
                  <Icon name="Lightbulb" size={14} color="#FCD34D" />
                  Tips
                </p>
                <ul className="space-y-2">
                  {[
                    'Download the CSV template for the correct format',
                    'Required: address, price, bedrooms, bathrooms, type',
                    'Use AI Generate to auto-write property descriptions',
                    'Drafts are saved automatically every 1.5 seconds',
                    'Preview listings before publishing to review all details'
                  ]?.map((tip, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-slate-400">
                      <span className="w-1 h-1 rounded-full bg-cyan-400 mt-1.5 shrink-0" />
                      {tip}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Quick Links */}
              <div className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <p className="text-white text-sm font-semibold mb-3">Quick Links</p>
                <div className="space-y-2">
                  <button onClick={() => navigate('/agent-dashboard')} className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-white/5 transition-colors text-left">
                    <Icon name="LayoutDashboard" size={14} color="#94A3B8" />
                    <span className="text-slate-400 text-xs">Agent Dashboard</span>
                  </button>
                  <button onClick={() => navigate('/create-listing')} className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-white/5 transition-colors text-left">
                    <Icon name="Plus" size={14} color="#94A3B8" />
                    <span className="text-slate-400 text-xs">Single Property Upload</span>
                  </button>
                  <button onClick={() => navigate('/list-your-property-landing')} className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-white/5 transition-colors text-left">
                    <Icon name="Home" size={14} color="#94A3B8" />
                    <span className="text-slate-400 text-xs">List Your Property</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Preview Modal */}
      {showPreview && (
        <PreviewModal
          properties={currentProperties}
          onClose={() => setShowPreview(false)}
          onPublish={handlePublish}
        />
      )}
    </div>
  );
};

export default BulkUploadProperties;
